"use client"
import type React from "react"
import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { getAllAnnouncements, deleteAnnouncement } from "api/apiHelper"
import { usePermissions } from "app/hooks"
import { toast } from "react-toastify"
import TabsContainer from "components/TabsContainer"
import Tab from "components/Tab"

interface Announcement {
  id: number
  title: string
  description: string
  created_at: string
  type: "info" | "warning" | "danger" | string
}

const AllAnnouncements: React.FC = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; announcement: Announcement | null }>({
    isOpen: false,
    announcement: null,
  })
  const [deleting, setDeleting] = useState(false)
  const [filterType, setFilterType] = useState<string | null>(null)

  const permissions = usePermissions() || []

  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        setLoading(true)
        const response = await getAllAnnouncements()
        setAnnouncements(response.data)
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to fetch announcements")
      } finally {
        setLoading(false)
      }
    }
    fetchAnnouncements()
  }, [])

  // Filter and sort announcements
  const filteredAndSortedAnnouncements = [...announcements]
    .filter((announcement) => (filterType === null ? true : announcement.type === filterType))
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())

  // Format date to be more readable
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      month: "short",
      day: "numeric",
      year: "numeric",
    }
    return new Date(dateString).toLocaleDateString(undefined, options)
  }

  // Get type styling
  const getTypeStyles = (type: string) => {
    switch (type) {
      case "danger":
        return {
          badge: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
          icon: "text-red-500",
        }
      case "warning":
        return {
          badge: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
          icon: "text-yellow-500",
        }
      case "info":
      default:
        return {
          badge: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
          icon: "text-blue-500",
        }
    }
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "danger":
        return "fas fa-exclamation-triangle"
      case "warning":
        return "fas fa-exclamation-circle"
      case "info":
      default:
        return "fas fa-info-circle"
    }
  }

  const handleDeleteClick = (announcement: Announcement) => {
    setDeleteModal({ isOpen: true, announcement })
  }

  const handleDeleteConfirm = async () => {
    if (!deleteModal.announcement) return

    try {
        setDeleting(true)
        await deleteAnnouncement(deleteModal.announcement.id)
    
        setAnnouncements((prev) => prev.filter((a) => a.id !== deleteModal.announcement!.id))
        setDeleteModal({ isOpen: false, announcement: null })
        toast.success(`"${deleteModal.announcement.title}" has been deleted successfully`)
    
      } catch (err) {
        console.error("Failed to delete announcement:", err)
        toast.error(
          err instanceof Error && err.message 
            ? `Failed to delete announcement: ${err.message}`
            : "Failed to delete announcement. Please try again."
        )
      } finally {
        setDeleting(false)
      }
  }

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, announcement: null })
  }

  // Component to render announcements list
  const AnnouncementsList = ({ announcements }: { announcements: Announcement[] }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
          {filterType === null
            ? "All Announcements"
            : `${filterType.charAt(0).toUpperCase() + filterType.slice(1)} Announcements`}
        </h2>

        {/* Announcements List */}
        <div className="space-y-4">
          {announcements.map((announcement, index) => {
            const typeStyles = getTypeStyles(announcement.type)
            return (
              <div
                key={announcement.id}
                className={`flex items-start space-x-4 p-4 rounded-lg border border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                  index % 2 === 0 ? "bg-gray-50/50 dark:bg-gray-800/50" : "bg-white dark:bg-gray-800"
                }`}
              >
                <div className="p-2 rounded-full bg-gray-100 dark:bg-gray-700">
                  <i className={`${getTypeIcon(announcement.type)} ${typeStyles.icon}`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white truncate">
                      {announcement.title}
                    </h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${typeStyles.badge}`}>
                      {announcement.type.charAt(0).toUpperCase() + announcement.type.slice(1)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-2 leading-relaxed">
                    {announcement.description}
                  </p>
                  <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                    <i className="fas fa-calendar-alt mr-2"></i>
                    {formatDate(announcement.created_at)}
                  </div>
                </div>
                {permissions && permissions.includes("bulletin.delete_announcement") && (
                  <button
                    onClick={() => handleDeleteClick(announcement)}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    title="Delete announcement"
                  >
                    <i className="fas fa-trash text-sm"></i>
                  </button>
                )}
              </div>
            )
          })}
        </div>

        {/* Empty State */}
        {announcements.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-bullhorn text-2xl text-gray-400"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {filterType === null ? "No announcements yet" : `No ${filterType} announcements`}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              {filterType === null
                ? "Get started by creating your first announcement."
                : `There are no ${filterType} announcements to display.`}
            </p>
            {permissions && permissions.includes("bulletin.add_announcement") && (
              <Link
                to="/bulletin/announcements/create/"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <i className="fas fa-plus mr-2"></i>
                Create Announcement
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  )

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-spinner fa-spin text-blue-600 dark:text-blue-400"></i>
            </div>
            <p className="text-gray-600 dark:text-gray-300">Loading announcements...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mt-5">
        <div className="flex items-center justify-center py-16">
          <div className="text-center max-w-md">
            <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-exclamation-triangle text-red-600 dark:text-red-400"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Something went wrong</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container mt-5">
      <div className="flex items-center justify-end">
        {permissions && permissions.includes("bulletin.add_announcement") && (
          <Link
            to="/bulletin/announcements/create/"
            className="flex items-center px-4 py-2 bg-brand-500 text-white hover:bg-brand-600 dark:bg-brand-400 dark:hover:bg-brand-500 rounded-lg transition-colors shadow-sm"
          >
            <i className="fas fa-plus mr-2"></i>
            New Announcement
          </Link>
        )}
      </div>

      {/* Tabs using TabsContainer and Tab components */}
      <TabsContainer>
        <Tab 
          isActive={filterType === null} 
          title="All" 
          onClick={() => setFilterType(null)}
        >
          <AnnouncementsList announcements={announcements.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())} />
        </Tab>
        <Tab 
          isActive={filterType === "info"} 
          title="Info" 
          onClick={() => setFilterType("info")}
        >
          <AnnouncementsList announcements={announcements.filter(a => a.type === "info").sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())} />
        </Tab>
        <Tab 
          isActive={filterType === "warning"} 
          title="Warnings" 
          onClick={() => setFilterType("warning")}
        >
          <AnnouncementsList announcements={announcements.filter(a => a.type === "warning").sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())} />
        </Tab>
        <Tab 
          isActive={filterType === "danger"} 
          title="Alerts" 
          onClick={() => setFilterType("danger")}
        >
          <AnnouncementsList announcements={announcements.filter(a => a.type === "danger").sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())} />
        </Tab>
      </TabsContainer>

      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && deleteModal.announcement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mr-4">
                  <i className="fas fa-exclamation-triangle text-red-600 dark:text-red-400"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Delete Announcement</h3>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">This action cannot be undone</p>
                </div>
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Are you sure you want to delete "{deleteModal.announcement.title}"?
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={handleDeleteCancel}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center"
                >
                  {deleting ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Deleting...
                    </>
                  ) : (
                    "Delete"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AllAnnouncements